import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import { useAors } from 'hooks/useAors';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  onboardingProgress,
  type OnboardingAction,
  type OnboardingCounts,
  type OrgOnboardingState,
} from './onboardingModel';

export const orgOnboardingKeys = {
  state: (groupGuid: string) => ['org-onboarding', groupGuid] as const,
};

export function useOrgOnboardingState(options?: { enabled?: boolean }) {
  const groupGuid = useAppSelector(selectGroupGuid);
  return useQuery({
    queryKey: orgOnboardingKeys.state(groupGuid ?? ''),
    queryFn: async () => {
      const resp = await axios.get<OrgOnboardingState>(
        config.apiGateway.org_onboarding,
        { params: { groupGuid } }
      );
      return resp.data;
    },
    enabled: !!groupGuid && (options?.enabled ?? true),
  });
}

export function useOrgOnboardingAction() {
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (action: OnboardingAction) => {
      const resp = await axios.put<OrgOnboardingState>(
        config.apiGateway.org_onboarding,
        { groupGuid, ...action }
      );
      return resp.data;
    },
    onSuccess: (state) => {
      queryClient.setQueryData(
        orgOnboardingKeys.state(state.group_guid),
        state
      );
    },
  });
}

/** Live step counts from org data plus the saved skips. */
export function useOrgOnboardingProgress(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const stateQuery = useOrgOnboardingState({ enabled });
  const aorsQuery = useAors();
  const assetsQuery = useResourceSets();
  const rulesQuery = useOrgEventAutomationRules({ enabled });
  const counts: OnboardingCounts = {
    aors: aorsQuery.data?.length ?? 0,
    assets: assetsQuery.data?.length ?? 0,
    rules: rulesQuery.data?.length ?? 0,
  };
  const skipped = stateQuery.data?.skipped_steps ?? [];
  return {
    state: stateQuery.data,
    counts,
    skipped,
    progress: onboardingProgress(counts, skipped),
    isLoading:
      stateQuery.isLoading ||
      aorsQuery.isLoading ||
      assetsQuery.isLoading ||
      rulesQuery.isLoading,
    aorsLoaded: aorsQuery.isSuccess,
  };
}
